import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { secureGet, secureGetPdf } from '../../config/api';
import { formatDateTime, formatAmount } from '../../utils/format';
import Breadcrumbs from '../../components/Breadcrumbs';
import LoadingLogo from '../../components/LoadingLogo';
import SnackbarNotification from '../../components/SnackbarNotification';

import exportIcon from '../../icons/export.svg';

const BillingInvoiceDetails = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams();

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const fetchInvoice = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await secureGet(`/billing-invoices/${id}`, showSnackbar, { signal });
      setInvoice(res?.data ?? null);
    } catch (err) {
      if (err?.name !== 'AbortError') {
        setInvoice(null);
      }
    } finally {
      setLoading(false);
    }
  }, [id, signal]);

  useEffect(() => {
    fetchInvoice();
    return () => abortController.abort();
  }, [fetchInvoice, abortController]);

  const handleDownloadPdf = async () => {
    if (!invoice?.id) return;
    try {
      const response = await secureGetPdf(`/billing-invoices/${invoice.id}/pdf`, showSnackbar);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${invoice.invoiceNumber || invoice.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSnackbar(t('confirmation.downloadSuccess'), 'success');
    } catch (error) {
      console.error('Failed to download invoice PDF:', error);
    }
  };

  const handleBack = () => {
    history.push({
      pathname: '/administration/billing-invoices',
      state: history.location.state,
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <LoadingLogo />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div>
        <Helmet>
          <title>GrayFox - {t('titles.billingInvoiceDetails')}</title>
        </Helmet>
        <div className="mb-4">
          <Breadcrumbs />
        </div>
        <p className="text-[#38485C]">{t('placeholder.noResults')}</p>
        <button
          type="button"
          onClick={handleBack}
          className="mt-4 text-primary font-semibold hover:underline"
        >
          {t('actions.back') || 'Back'}
        </button>
        <SnackbarNotification
          open={snackbar.open}
          message={snackbar.message}
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        />
      </div>
    );
  }

  const getStatusStyle = (status) => {
    const s = (status || '').toUpperCase();
    if (s === 'PAID' || s === 'FINALIZED') return 'bg-[#06D6A0] text-white';
    if (s === 'DRAFT') return 'bg-[#A3A3A3] text-white';
    if (s === 'OVERDUE') return 'bg-[#EF476F] text-white';
    return 'bg-[#707070] text-white';
  };

  return (
    <div>
      <Helmet>
        <title>GrayFox - {t('titles.billingInvoiceDetails')}</title>
      </Helmet>

      <div className="mb-4">
        <Breadcrumbs />
        <h1 className="text-[40px] font-semibold text-black mt-2">
          {t('titles.billingInvoiceDetails')}
        </h1>

        <div className="flex justify-between items-center mt-4">
          <div className="flex items-center space-x-[10px]">
            <p className="text-black font-semibold text-[24px]">
              {invoice.invoiceNumber || `#${invoice.id}`}
            </p>
            <span className={`px-2 py-1 rounded-[10px] font-semibold text-[12px] ${getStatusStyle(invoice.status)}`}>
              {invoice.status || '—'}
            </span>
          </div>
          <div className="flex space-x-4">
            <button
              type="button"
              className="flex items-center justify-center bg-white text-[#043F88] px-6 py-3 font-bold text-[14px] rounded-[12px] gap-x-2 shadow-[0px_20px_80px_#00000011]"
              onClick={handleDownloadPdf}
            >
              <img src={exportIcon} alt="download" className="w-5 h-5" />
              {t('actions.downloadInvoice')}
            </button>
            <button
              type="button"
              className="flex items-center justify-center border border-[#E3E3E3] text-[#38485C] px-6 py-3 font-bold text-[14px] rounded-[12px]"
              onClick={handleBack}
            >
              {t('actions.back') || 'Back'}
            </button>
          </div>
        </div>
      </div>

      <div className="w-full flex flex-wrap justify-between bg-white py-4 pl-[32px] pr-4 rounded-[12px] shadow-[0px_6px_26px_#626B7D12] mb-4">
        <div className="flex-1 min-w-[140px] mb-4">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">{t('billingInvoices.issuedAt')}</p>
          <p className="text-[#38485C] text-[20px] font-semibold">{formatDateTime(invoice.issuedAt)}</p>
        </div>
        <div className="flex-1 min-w-[140px] mb-4">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">{t('billingInvoices.dueDate')}</p>
          <p className="text-[#38485C] text-[20px] font-semibold">{formatDateTime(invoice.dueDate)}</p>
        </div>
        <div className="flex-1 min-w-[140px] mb-4">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">{t('billingInvoices.periodEnd')}</p>
          <p className="text-[#38485C] text-[20px] font-semibold">{formatDateTime(invoice.periodEnd)}</p>
        </div>
        <div className="flex-1 min-w-[140px] mb-4">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">{t('billingInvoices.priceHt')}</p>
          <p className="text-[#38485C] text-[20px] font-semibold">{formatAmount(invoice.priceHt)}</p>
        </div>
        <div className="flex-1 min-w-[140px] mb-4">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">{t('billingInvoices.priceTtc')}</p>
          <p className="text-[#38485C] text-[20px] font-semibold">{formatAmount(invoice.priceTtc)}</p>
        </div>
        <div className="flex-1 min-w-[140px] mb-4">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">{t('billingInvoices.extraSeatsCount')}</p>
          <p className="text-[#38485C] text-[20px] font-semibold">{invoice.extraSeatsCount ?? '—'}</p>
        </div>
      </div>

      {invoice.details && invoice.details.length > 0 && (
        <>
          <h3 className="text-[16px] font-medium text-[#ACAFC6] mb-4">
            {t('billingInvoices.details') || 'Invoice details'}
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="bg-primary/10 text-[#707070]">
                  <th className="px-4 py-3 text-sm font-semibold rounded-tl-[12px]">ID</th>
                  <th className="px-4 py-3 text-sm font-semibold">{t('billingInvoices.itemType') || 'Type'}</th>
                  <th className="px-4 py-3 text-sm font-semibold text-right">{t('billingInvoices.price') || 'Price'}</th>
                  <th className="px-4 py-3 text-sm font-semibold text-right rounded-tr-[12px]">{t('billingInvoices.quantity') || 'Quantity'}</th>
                </tr>
              </thead>
              <tbody>
                {invoice.details.map((detail, index) => (
                  <tr key={detail.id ?? index} className="border-b border-[#C5C5C5]/25">
                    <td className="px-4 py-3 text-[#38485C] font-semibold">{detail.id}</td>
                    <td className="px-4 py-3 text-[#38485C] font-semibold">{detail.itemType ?? '—'}</td>
                    <td className="px-4 py-3 text-[#043F88] font-bold text-sm text-right">{formatAmount(detail.price)}</td>
                    <td className="px-4 py-3 text-[#38485C] font-semibold text-right">{detail.quantity ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </div>
  );
};

export default BillingInvoiceDetails;
